import React, { useMemo } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Skeleton,
  Box,
  Alert
} from '@mui/material';
import { useEndReasonTypeData } from '../contexts/EndReasonTypeDataContext';
import { EndReasonTypeFilterControls } from './EndReasonTypeFilterControls';
import { DashboardCard } from '../components/DashboardCard';

export const EndReasonTypeTableCard = () => {
  const { data, loading, error, filter, totalsByYear } = useEndReasonTypeData();

  // Memoized derived data
  const { years, endReasonTypes } = useMemo(() => {
    if (!data) return { years: [], endReasonTypes: [] };
    
    return {
      years: [...new Set(data.map((d) => d.year))].sort(),
      endReasonTypes: [...new Set(data.map((d) => d.endReasonTypeName))].sort()
    };
  }, [data]);

  // Export handler - passed directly as prop to DashboardCard
  const handleExport = () => {
    if (!data || data.length === 0) return;

    const headers = ['Year', 'End Reason Type', 'Student Count', 'Total Enrolled', '% Submitted'];
    
    const rows = data.map(item => [
      item.year,
      `"${item.endReasonTypeName}"`,
      item.studentCount,
      item.totalEnrolled,
      item.percentageSubmitted.toFixed(2)
    ]);

    const csvContent = [
      headers.join(','), 
      ...rows.map(row => row.join(','))
    ].join('\n');

    // Create a temporary link to trigger download
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', 'EndReasonType_Data.csv');
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const renderTableContent = () => {
    // Single year view
    if (filter.schoolYears.length === 1) {
      const selectedYear = filter.schoolYears[0];
      const yearData = data!.filter((d) => d.year === selectedYear);
      const yearTotal = totalsByYear.find(t => t.year === selectedYear);

      return (
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 'bold' }}>End Reason Type</TableCell>
                <TableCell align="right" sx={{ fontWeight: 'bold' }}>Students</TableCell>
                <TableCell align="right" sx={{ fontWeight: 'bold' }}>Number Enrolled</TableCell>
                <TableCell align="right" sx={{ fontWeight: 'bold' }}>% Submitted</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {yearData.map((item) => (
                <TableRow key={item.endReasonTypeId}>
                  <TableCell>{item.endReasonTypeName}</TableCell>
                  <TableCell align="right">{item.studentCount}</TableCell>
                  <TableCell align="right">{item.totalEnrolled}</TableCell>
                  <TableCell align="right">
                    <strong>{item.percentageSubmitted.toFixed(1)}%</strong>
                  </TableCell>
                </TableRow>
              ))}
              <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                <TableCell sx={{ fontWeight: 'bold' }}>Total</TableCell>
                <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                  {yearTotal?.totalStudents ?? 0}
                </TableCell>
                <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                  {yearTotal?.totalEnrolled ?? 0}
                </TableCell>
                <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                  {(yearTotal?.totalPercentage ?? 0).toFixed(1)}%
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      );
    }

    // Multi-year comparison view
    return (
      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 'bold', minWidth: 200 }}>End Reason Type</TableCell>
              {years.map((year) => (
                <TableCell key={year} align="center" sx={{ fontWeight: 'bold' }}>
                  {`${year}-${year + 1}`}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {endReasonTypes.map((endReasonType) => (
              <TableRow key={endReasonType}>
                <TableCell>{endReasonType}</TableCell>
                {years.map((year) => {
                  const cellData = data!.find(
                    (d) => d.year === year && d.endReasonTypeName === endReasonType
                  );
                  if (!cellData) {
                    return <TableCell key={year} align="center">-</TableCell>;
                  }
                  return (
                    <TableCell key={year} align="center">
                      {`${cellData.studentCount} (${cellData.percentageSubmitted.toFixed(1)}%)`}
                    </TableCell>
                  );
                })}
              </TableRow>
            ))}
            <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
              <TableCell sx={{ fontWeight: 'bold' }}>Total</TableCell>
              {years.map((year) => {
                const yearTotal = totalsByYear.find((t) => t.year === year);
                if (!yearTotal) {
                  return (
                    <TableCell key={year} align="center" sx={{ fontWeight: 'bold' }}>
                      -
                    </TableCell>
                  );
                }
                return (
                  <TableCell key={year} align="center" sx={{ fontWeight: 'bold' }}>
                    {`${yearTotal.totalStudents} (${(yearTotal.totalPercentage ?? 0).toFixed(1)}%)`}
                  </TableCell>
                );
              })}
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>
    );
  };

  const renderDataContent = () => {
    if (loading) {
      return <Skeleton variant="rounded" height={300} />;
    }

    if (error) {
      return (
        <Alert severity="error" sx={{ m: 2 }}>
          {error}
        </Alert>
      );
    }

    if (!data || data.length === 0) {
      return (
        <Typography align="center" color="textSecondary" sx={{ py: 5 }}>
          No end reason type data available for selected years
        </Typography>
      );
    }

    return (
      <>
        {renderTableContent()}
        <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
          Note: Percentages based on total enrolled students per year
        </Typography>
      </>
    );
  };

  const renderContent = () => {
    return (
      <Box sx={{ height: '100%' }}>
        <EndReasonTypeFilterControls />
        {renderDataContent()}
      </Box>
    );
  };

  return (
    <DashboardCard title="End Reason Type" onExport={handleExport}>
      {renderContent()}
    </DashboardCard>
  );
};
